import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import * as React from 'react'

import {useFetchEffect} from '../../../../../hocs/withEffect'
import {useIsAgentsPartSelected} from '../../../../../hooks/routes'
import {PlaceIdList} from '../../../../../plugins/plugins.types'
import {getIsSakuraUI} from '../../../../../selectors'
import {subscribeonStatisticsUpdates} from '../../../../../utils/subscriber'
import Plugin from '../../../../common/Plugin/Plugin'
import {getCachedPlugins} from '../../../../common/Plugin/Plugin.selectors'
import {headerAgentsLinkHint} from '../../../../packages/Hints/HintItems/Hints.navigation'
import {useHint} from '../../../../packages/Hints/Hints.hooks'
import {LINKS} from '../../Header.consts'
import {getTopNavHelperData} from '../../Header.utils'
import HeaderLinkHost from '../LinkHost/LinkHost'
import styles from '../Links.css'
import type {StatisticsUpdateData} from '../Links.types'
import {isActiveClassicUILink} from '../Links.utils'

import {calculateContent} from './AgentsLink.utils'
import ProgressBar from './ProgressBar/ProgressBar'

import {useAppSelector} from 'src/hooks/react-redux'

function AgentLink() {
  useHint(headerAgentsLinkHint)
  const isSakuraUI = useAppSelector(getIsSakuraUI)
  const [statistics, setStatistics] = React.useState<StatisticsUpdateData | null | undefined>(null)
  const {agentsNum = getTopNavHelperData().agents, freeAgentsNum} = statistics ?? {}
  const customAgentsCounter = useAppSelector(
    state => getCachedPlugins(state, PlaceIdList.SAKURA_CUSTOM_AGENTS_COUNTER).length > 0,
  )
  const agentsPartSelected = useIsAgentsPartSelected()
  const active = isSakuraUI ? agentsPartSelected : isActiveClassicUILink('agents')
  useFetchEffect(() => {
    if (customAgentsCounter) {
      return undefined
    }

    return subscribeonStatisticsUpdates((data: StatisticsUpdateData | null | undefined) =>
      setStatistics(data),
    )
  }, [customAgentsCounter])
  const {agentsLoad, title} = React.useMemo(
    () => calculateContent(agentsNum, freeAgentsNum),
    [agentsNum, freeAgentsNum],
  )
  const postLink = React.useMemo(() => {
    if (customAgentsCounter) {
      return <Plugin placeId="SAKURA_CUSTOM_AGENTS_COUNTER" withoutLoader />
    }

    if (agentsNum == null) {
      return <LoaderInline />
    }

    const shouldRenderProgress = !isNaN(agentsLoad) && agentsLoad > 0
    return (
      <>
        {shouldRenderProgress && (
          <ProgressBar
            percent={agentsLoad || 0}
            size={16}
            activeCircleClassName={styles.progressBar}
          />
        )}
        <span data-hint-container-id={headerAgentsLinkHint.id} className={styles.counter}>
          {agentsNum}
        </span>
        {!shouldRenderProgress && <span className={styles.progressPlaceholder} />}
      </>
    )
  }, [agentsNum, agentsLoad, customAgentsCounter])

  return React.useMemo(
    () => (
      <div title={customAgentsCounter ? undefined : title} className={styles.container}>
        <HeaderLinkHost active={active} title={'Agents'} to={LINKS.agents} className={styles.link}>
          {'Agents'}
        </HeaderLinkHost>
        <div className={styles.postLink}>{postLink}</div>
      </div>
    ),
    [title, active, postLink, customAgentsCounter],
  )
}

export default React.memo(AgentLink)
